import { IconButton, Stack } from '@mui/material';
import { Link } from 'react-router-dom';

import { MarketingContentImgPreview } from '@occc-app/design-system';
import {
  ReceiptLongIcon,
  Typography,
  DataTableHeader,
  MakeTableFields,
} from '@occc/ui-design-system';
import { MembershipVoucherFragment, Voucher_State, Vouchers_Sort_By } from '@occc/graphql';
import {
  calcDateTime,
  DateTimeFormatTZ,
  formatDateTimeTZ,
  getUserTimezone,
  parseDate,
} from '@occc-app/lib/date';
import { DateRange } from '@occc-app/routes/Member/MemberDetailsPage/components/OffersAndVouchers/Vouchers/types';
import { routeCreator } from '@occc-app/routes/Member/utils';

import { titleBySourceTypeValue } from './constants';

const voucherStateMap: Record<Voucher_State, string> = {
  [Voucher_State.Expired]: 'Expired',
  [Voucher_State.Abandoned]: 'Abandoned',
  [Voucher_State.Fulfilled]: 'Fulfilled',
  [Voucher_State.Unfulfilled]: 'Unfulfilled',
};

export const makeFields: MakeTableFields<{
  voucher: MembershipVoucherFragment;
}> = ({ voucher }) => [
  {
    label: 'Image',
    value: (
      <MarketingContentImgPreview
        imageUrl={voucher.offer?.marketingContents?.[0]?.imageUrl ?? undefined}
      />
    ),
  },
  {
    label: 'Name',
    value: voucher.offer?.name || '',
  },
  {
    label: 'State',
    value: (
      <Stack spacing={0.5} direction='row' alignItems='center' justifyContent='space-between'>
        <Typography variant='body2'>{voucherStateMap[voucher.state]}</Typography>
        {voucher.state === Voucher_State.Fulfilled &&
          voucher.fulfilledBy?.__typename === 'ActivateTransaction' && (
            <IconButton
              size='small'
              onClick={(e) => e.stopPropagation()}
              component={Link}
              to={routeCreator.transaction(voucher.fulfilledBy.id)}
              target='_blank'
            >
              <ReceiptLongIcon fontSize='small' color='primary' />
            </IconButton>
          )}
      </Stack>
    ),
  },
  {
    label: 'Source',
    value: voucher.sourceType
      ? titleBySourceTypeValue[voucher.sourceType] || voucher.sourceType
      : '',
  },
  {
    label: 'Creation Date',
    value: formatDateTimeTZ(
      parseDate(voucher.createdAt),
      getUserTimezone(),
      DateTimeFormatTZ.DateTimeWithShortTimeZone,
    ),
  },
  {
    label: 'Expiration Date',
    value: voucher.expireAt
      ? formatDateTimeTZ(
          parseDate(voucher.expireAt),
          getUserTimezone(),
          DateTimeFormatTZ.DateTimeWithShortTimeZone,
        )
      : '',
  },
];

export const headers: DataTableHeader[] = [
  { label: 'Image' },
  { label: 'Name' },
  { label: 'State' },
  { label: 'Source' },
  {
    label: 'Creation Date',
    sortingField: Vouchers_Sort_By.CreatedAt,
  },
  { label: 'Expiration Date' },
];

export const getDateRange = (range: DateRange): { start: Date | null; end: Date | null } => {
  const now = new Date();
  if (range === DateRange.Last1H) {
    return {
      start: calcDateTime(now, 'sub', 1, 'hour'),
      end: now,
    };
  }
  if (range === DateRange.Last6H) {
    return {
      start: calcDateTime(now, 'sub', 6, 'hour'),
      end: now,
    };
  }
  if (range === DateRange.Last24H) {
    return {
      start: calcDateTime(now, 'sub', 24, 'hour'),
      end: now,
    };
  }
  if (range === DateRange.Prev24H) {
    return {
      start: calcDateTime(now, 'sub', 48, 'hour'),
      end: calcDateTime(now, 'sub', 24, 'hour'),
    };
  }

  return {
    start: null,
    end: null,
  };
};
